import * as React from 'react';
import type {
  HistoryAddressValue,
  HistoryStatusValue,
  HistoryValue,
  PostalAddress,
} from '@klareospace/pharos-lib-types';
import { formatPostalAddress } from '@klareospace/pharos-lib-utils';
import { ArrowRight, CircleDot, Minus, PenLine, Plus } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { Skeleton } from '../../ui/skeleton';

// ─── Constants ────────────────────────────────────────────────────────────────

type ChipTone = 'added' | 'removed' | 'updated';

type AddressMainLineField = 'city' | 'country' | 'houseNumber' | 'postalCode' | 'street';

/**
 * `PostalAddress` detail fields surfaced as per-field diff lines on an address history entry.
 * Derived from `PostalAddress` itself (minus the main-line fields and `userComment`, the shared
 * driver comment with its own flow) so that adding a field to `PostalAddress` breaks this
 * typecheck until the new field is explicitly classified as detail / main-line / excluded below.
 */
export type AddressDetailField = Exclude<keyof PostalAddress, AddressMainLineField | 'userComment'>;

/**
 * Exhaustiveness guard: `satisfies Record<AddressDetailField, true>` forces every member of the
 * derived `AddressDetailField` union to be listed here, so the field list below stays in sync
 * with `PostalAddress` at compile time.
 */
const ADDRESS_DETAIL_FIELD_SET = {
  additionalInformations: true,
  complement: true,
  digicode: true,
  floor: true,
  hasElevator: true,
} as const satisfies Record<AddressDetailField, true>;

const ADDRESS_DETAIL_FIELDS = Object.keys(ADDRESS_DETAIL_FIELD_SET) as readonly AddressDetailField[];

const CHIP_TONE_CLASSES: Record<ChipTone, string> = {
  added: 'border-success/30 bg-success/10 text-success',
  removed: 'border-destructive/30 bg-destructive/10 text-destructive',
  updated: 'border-border bg-muted text-muted-foreground',
};

const CHIP_TONE_ICONS: Record<ChipTone, typeof Plus> = {
  added: Plus,
  removed: Minus,
  updated: PenLine,
};

type ValueRenderer = (
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
) => React.ReactNode;

const VALUE_RENDERERS: Record<HistoryValue['kind'], ValueRenderer> = {
  actorRef: renderActorRefEntry,
  address: renderAddressEntry,
  amount: renderAmountEntry,
  listDiff: renderListDiffEntry,
  measurement: renderMeasurementEntry,
  scalar: renderScalarEntry,
  status: renderStatusEntry,
  tags: renderTagsEntry,
  timeslot: renderTimeslotEntry,
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface HistoryFeedFormatters {
  /**
   * Pre-translated labels for the per-field address detail diff lines (`floor`, `digicode`,
   * `complement`, `additionalInformations`, `hasElevator`). Falls back to the raw field name when
   * a label is missing.
   */
  addressFieldLabels?: Partial<Record<AddressDetailField, string>>;
  /** Formats a postal address snapshot into a one-line string. Defaults to `formatPostalAddress`. */
  formatAddress?: (address: PostalAddress) => string;
  /** Formats an amount value into a display string. Defaults to `"${value} ${currency}"`. */
  formatAmount?: (value: number, currency: string) => string;
  /** Formats a boolean detail-field value (currently only `hasElevator`). Defaults to `String(value)`. */
  formatBoolean?: (value: boolean) => string;
  /** Formats a measurement value into a display string. Defaults to `"${value} ${unit}"`. */
  formatMeasurement?: (value: number, unit: string) => string;
  /** Formats a scalar value into a display string. Defaults to `String(value)`. */
  formatScalar?: (value: number | string) => string;
  /** Formats a raw status code into a display label. Defaults to the raw status string. */
  formatStatus?: (status: string) => string;
  /** Formats a timeslot start/end pair into a display string. Defaults to `"${start} - ${end}"`. */
  formatTimeslot?: (start: string, end: string) => string;
  /**
   * Status → badge className map applied directly to the status chips (previous and new, each
   * resolved independently from its own status value). An unknown status simply misses the map
   * and the chip keeps its default secondary/outline styling.
   */
  statusBadgeColors?: Record<string, string>;
}

/** Pre-translated, sr-only-announced labels for the added/removed/updated operations on diff chips. */
export interface HistoryFeedDiffOperationLabels {
  /** Announced for each added-item chip (e.g. "Added") */
  added: string;
  /** Announced for each removed-item chip (e.g. "Removed") */
  removed: string;
  /** Announced for each updated-item chip (e.g. "Updated") */
  updated: string;
}

/**
 * One row rendered by `HistoryFeed`. `label`, `actorLabel`, and `timestamp` are pre-translated /
 * pre-formatted by the caller — the component itself does not perform any i18n or date formatting.
 */
export interface HistoryFeedEntry {
  /** Pre-resolved, pre-translated actor display name (e.g. a user's full name, or a translated "System"/fallback label) */
  actorLabel: string;
  /** Stable unique identifier used as the React list key */
  id: string;
  /** Pre-translated label describing the event (e.g. "Status changed") */
  label: string;
  /** Raw new value, switched on `kind` — `null` when the event clears the field or carries no value */
  newValue: HistoryValue | null;
  /** Raw previous value, switched on `kind` — `null` when the entry is the initial/creation snapshot */
  previousValue: HistoryValue | null;
  /** Pre-formatted timestamp string */
  timestamp: string;
}

export interface HistoryFeedProps {
  /** Optional additional CSS classes */
  className?: string;
  /** Pre-translated sr-only labels announced on listDiff/tags chips for each operation */
  diffOperationLabels: HistoryFeedDiffOperationLabels;
  /** Pre-translated message shown when `entries` is empty and `isLoading` is false */
  emptyMessage: string;
  /** The entries to render, in the order given — ordering is the caller's responsibility */
  entries: HistoryFeedEntry[];
  /** Optional per-kind formatters for locale-sensitive value rendering */
  formatters?: HistoryFeedFormatters;
  /** Shows skeleton rows instead of `entries` while true */
  isLoading?: boolean;
  /** Number of skeleton rows to render while loading (default: 4) */
  skeletonRowCount?: number;
}

/** Internal bundle threaded through every per-kind renderer. */
interface HistoryFeedRenderContext {
  readonly diffOperationLabels: HistoryFeedDiffOperationLabels;
  readonly formatters: HistoryFeedFormatters;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Vertical feed of `HistoryEntry` rows. Strictly presentation-only: no i18n and no date
 * formatting happens inside this component — `label`, `actorLabel`, and `timestamp` on each
 * `HistoryFeedEntry` must already be translated/formatted by the caller. Typed `previousValue`/
 * `newValue` are rendered raw, switched on `HistoryValue.kind`; pass `formatters` for the few
 * kinds where locale-sensitive formatting is needed (timeslot, address, scalar, status label).
 */
export function HistoryFeed({
  className,
  diffOperationLabels,
  emptyMessage,
  entries,
  formatters = {},
  isLoading = false,
  skeletonRowCount = 4,
}: HistoryFeedProps): React.JSX.Element {
  if (isLoading) {
    return <HistoryFeedSkeleton className={className} rowCount={skeletonRowCount} />;
  }

  if (entries.length === 0) {
    return <p className={cn('text-sm text-muted-foreground', className)}>{emptyMessage}</p>;
  }

  const context: HistoryFeedRenderContext = { diffOperationLabels, formatters };

  return (
    <ol className={cn('space-y-2', className)}>
      {entries.map((entry) => (
        <HistoryFeedRow context={context} entry={entry} key={entry.id} />
      ))}
    </ol>
  );
}

// ─── Value narrowing ──────────────────────────────────────────────────────────

function isValueOfKind<K extends HistoryValue['kind']>(
  value: HistoryValue | null,
  kind: K,
): value is Extract<HistoryValue, { kind: K }> {
  return value !== null && value.kind === kind;
}

function formatUnknownValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return '';
  return JSON.stringify(value);
}

function defaultFormatTimeslot(start: string, end: string): string {
  return `${start} - ${end}`;
}

function defaultFormatStatus(status: string): string {
  return status;
}

function defaultFormatScalar(value: number | string): string {
  return String(value);
}

function defaultFormatMeasurement(value: number, unit: string): string {
  return `${value} ${unit}`;
}

function defaultFormatAmount(value: number, currency: string): string {
  return `${value} ${currency}`;
}

function defaultFormatBoolean(value: boolean): string {
  return String(value);
}

// ─── Value renderers ──────────────────────────────────────────────────────────

interface ValuePairProps {
  readonly next: React.ReactNode;
  readonly previous: React.ReactNode;
}

function ValuePair({ next, previous }: ValuePairProps): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-sm">
      {previous !== null && (
        <>
          <span className="text-muted-foreground line-through">{previous}</span>
          <ArrowRight aria-hidden="true" className="w-3.5 h-3.5 text-muted-foreground shrink-0 rtl:rotate-180" />
        </>
      )}
      {next !== null && <span>{next}</span>}
    </div>
  );
}

function resolveStatusReasonText(curr: HistoryStatusValue | null): string | undefined {
  if (curr === null) return undefined;
  // Only the current side is ever shown — a previous entry's reason/message describe the
  // transition INTO that status, not the one being rendered, so they are never surfaced here.
  // `reason` is the structured/translated label and `message` is free-text detail; compose both
  // when present (reason first, em-dash separator — matches the frontoffice's
  // `composeStatusErrorMessage` convention), otherwise show whichever one is present.
  const parts = [curr.reason, curr.message].filter(
    (part): part is string => typeof part === 'string' && part.length > 0,
  );
  return parts.length > 0 ? parts.join(' — ') : undefined;
}

interface StatusValueBadgesProps {
  readonly curr: HistoryStatusValue | null;
  readonly formatStatus: (status: string) => string;
  readonly prev: HistoryStatusValue | null;
  readonly statusBadgeColors?: Record<string, string>;
}

function StatusValueBadges({ curr, formatStatus, prev, statusBadgeColors }: StatusValueBadgesProps): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {prev !== null && (
        <>
          <Badge className={cn('w-fit font-normal', statusBadgeColors?.[prev.status])} variant="secondary">
            {formatStatus(prev.status)}
          </Badge>
          <ArrowRight aria-hidden="true" className="w-3.5 h-3.5 text-muted-foreground shrink-0 rtl:rotate-180" />
        </>
      )}
      {curr !== null && (
        <Badge className={cn('w-fit font-normal', statusBadgeColors?.[curr.status])} variant="outline">
          {formatStatus(curr.status)}
        </Badge>
      )}
    </div>
  );
}

function renderStatusEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const prev = isValueOfKind(previous, 'status') ? previous : null;
  const curr = isValueOfKind(next, 'status') ? next : null;
  if (prev === null && curr === null) return null;

  const formatStatus = context.formatters.formatStatus ?? defaultFormatStatus;
  const reasonText = resolveStatusReasonText(curr);

  return (
    <div className="flex flex-col gap-1">
      <StatusValueBadges
        curr={curr}
        formatStatus={formatStatus}
        prev={prev}
        statusBadgeColors={context.formatters.statusBadgeColors}
      />
      {reasonText !== undefined && <p className="text-xs text-muted-foreground italic">{reasonText}</p>}
    </div>
  );
}

function renderTimeslotEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const prev = isValueOfKind(previous, 'timeslot') ? previous : null;
  const curr = isValueOfKind(next, 'timeslot') ? next : null;
  if (prev === null && curr === null) return null;

  const formatTimeslot = context.formatters.formatTimeslot ?? defaultFormatTimeslot;

  return (
    <ValuePair
      next={curr !== null ? formatTimeslot(curr.start, curr.end) : null}
      previous={prev !== null ? formatTimeslot(prev.start, prev.end) : null}
    />
  );
}

/**
 * Formats one `PostalAddress` detail-field value, or `null` when unset/empty (no diff line is
 * shown for it). An explicit `null` is treated the same as `undefined`/`''` — stored snapshots can
 * carry explicit BSON `null`, and letting it fall through to `String(null)` would render the
 * literal text "null".
 */
function formatAddressDetailFieldValue(
  value: PostalAddress[AddressDetailField] | null | undefined,
  formatBoolean: (value: boolean) => string,
): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'boolean') return formatBoolean(value);
  return String(value);
}

interface ResolvedAddressFormatters {
  readonly addressFieldLabels: Partial<Record<AddressDetailField, string>>;
  readonly formatAddress: (address: PostalAddress) => string;
  readonly formatBoolean: (value: boolean) => string;
}

/** Builds one labeled `ValuePair` row per changed detail field (floor, digicode, complement, additionalInformations, hasElevator). */
function buildAddressDetailFieldRows(
  prev: HistoryAddressValue | null,
  curr: HistoryAddressValue | null,
  formatters: ResolvedAddressFormatters,
): React.ReactNode[] {
  return ADDRESS_DETAIL_FIELDS.reduce<React.ReactNode[]>((rows, field) => {
    const prevFormatted = formatAddressDetailFieldValue(prev?.address[field], formatters.formatBoolean);
    const currFormatted = formatAddressDetailFieldValue(curr?.address[field], formatters.formatBoolean);
    if (prevFormatted === currFormatted) return rows;

    const label = formatters.addressFieldLabels[field] ?? field;
    rows.push(
      <div className="flex flex-wrap items-center gap-1.5 text-sm" key={field}>
        <span className="text-muted-foreground">{label}:</span>
        <ValuePair next={currFormatted} previous={prevFormatted} />
      </div>,
    );
    return rows;
  }, []);
}

function resolveAddressFormatters(formatters: HistoryFeedFormatters): ResolvedAddressFormatters {
  return {
    addressFieldLabels: formatters.addressFieldLabels ?? {},
    formatAddress: formatters.formatAddress ?? formatPostalAddress,
    formatBoolean: formatters.formatBoolean ?? defaultFormatBoolean,
  };
}

interface AddressMainLines {
  readonly currMainLine: string | null;
  readonly differs: boolean;
  readonly prevMainLine: string | null;
}

/** Formats both address snapshots to their one-line main address and flags whether they differ. */
function resolveAddressMainLines(
  prev: HistoryAddressValue | null,
  curr: HistoryAddressValue | null,
  formatAddress: (address: PostalAddress) => string,
): AddressMainLines {
  const prevMainLine = prev !== null ? formatAddress(prev.address) : null;
  const currMainLine = curr !== null ? formatAddress(curr.address) : null;
  return { currMainLine, differs: prevMainLine !== currMainLine, prevMainLine };
}

function renderAddressEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const prev = isValueOfKind(previous, 'address') ? previous : null;
  const curr = isValueOfKind(next, 'address') ? next : null;
  if (prev === null && curr === null) return null;

  const addressFormatters = resolveAddressFormatters(context.formatters);
  const mainLines = resolveAddressMainLines(prev, curr, addressFormatters.formatAddress);
  const detailFieldRows = buildAddressDetailFieldRows(prev, curr, addressFormatters);

  if (!mainLines.differs && detailFieldRows.length === 0) {
    // Defensive: nothing differs — still render the main line so the entry is never empty.
    return <ValuePair next={mainLines.currMainLine} previous={mainLines.prevMainLine} />;
  }

  return (
    <div className="flex flex-col gap-1">
      {mainLines.differs && <ValuePair next={mainLines.currMainLine} previous={mainLines.prevMainLine} />}
      {detailFieldRows}
    </div>
  );
}

function renderScalarEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const prev = isValueOfKind(previous, 'scalar') ? previous : null;
  const curr = isValueOfKind(next, 'scalar') ? next : null;
  if (prev === null && curr === null) return null;

  const formatScalar = context.formatters.formatScalar ?? defaultFormatScalar;

  return (
    <ValuePair
      next={curr !== null ? formatScalar(curr.value) : null}
      previous={prev !== null ? formatScalar(prev.value) : null}
    />
  );
}

function renderMeasurementEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const prev = isValueOfKind(previous, 'measurement') ? previous : null;
  const curr = isValueOfKind(next, 'measurement') ? next : null;
  if (prev === null && curr === null) return null;

  const formatMeasurement = context.formatters.formatMeasurement ?? defaultFormatMeasurement;

  return (
    <ValuePair
      next={curr !== null ? formatMeasurement(curr.value, curr.unit) : null}
      previous={prev !== null ? formatMeasurement(prev.value, prev.unit) : null}
    />
  );
}

function renderAmountEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const prev = isValueOfKind(previous, 'amount') ? previous : null;
  const curr = isValueOfKind(next, 'amount') ? next : null;
  if (prev === null && curr === null) return null;

  const formatAmount = context.formatters.formatAmount ?? defaultFormatAmount;

  return (
    <ValuePair
      next={curr !== null ? formatAmount(curr.value, curr.currency) : null}
      previous={prev !== null ? formatAmount(prev.value, prev.currency) : null}
    />
  );
}

function renderActorRefEntry(previous: HistoryValue | null, next: HistoryValue | null): React.ReactNode {
  const prev = isValueOfKind(previous, 'actorRef') ? previous : null;
  const curr = isValueOfKind(next, 'actorRef') ? next : null;
  if (prev === null && curr === null) return null;

  return <ValuePair next={curr !== null ? curr.name : null} previous={prev !== null ? prev.name : null} />;
}

interface DiffChipProps {
  readonly label: string;
  readonly operationLabel: string;
  readonly tone: ChipTone;
}

function DiffChip({ label, operationLabel, tone }: DiffChipProps): React.JSX.Element {
  const Icon = CHIP_TONE_ICONS[tone];
  return (
    <Badge className={cn('gap-1 w-fit font-normal', CHIP_TONE_CLASSES[tone])} variant="outline">
      <Icon aria-hidden="true" className="w-3 h-3" />
      <span className="sr-only">{operationLabel}</span>
      {label}
    </Badge>
  );
}

function renderListDiffEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const value = isValueOfKind(next, 'listDiff') ? next : isValueOfKind(previous, 'listDiff') ? previous : null;
  if (value === null) return null;
  if (value.added.length === 0 && value.removed.length === 0 && value.updated.length === 0) return null;

  const { diffOperationLabels } = context;

  return (
    <div className="flex flex-wrap gap-1.5">
      {value.added.map((item, index) => (
        <DiffChip
          key={`added-${index}`}
          label={formatUnknownValue(item)}
          operationLabel={diffOperationLabels.added}
          tone="added"
        />
      ))}
      {value.removed.map((item, index) => (
        <DiffChip
          key={`removed-${index}`}
          label={formatUnknownValue(item)}
          operationLabel={diffOperationLabels.removed}
          tone="removed"
        />
      ))}
      {value.updated.map((item, index) => (
        <DiffChip
          key={`updated-${index}`}
          label={formatUnknownValue(item)}
          operationLabel={diffOperationLabels.updated}
          tone="updated"
        />
      ))}
    </div>
  );
}

function renderTagsEntry(
  previous: HistoryValue | null,
  next: HistoryValue | null,
  context: HistoryFeedRenderContext,
): React.ReactNode {
  const value = isValueOfKind(next, 'tags') ? next : isValueOfKind(previous, 'tags') ? previous : null;
  if (value === null) return null;
  if (value.added.length === 0 && value.removed.length === 0) return null;

  const { diffOperationLabels } = context;

  return (
    <div className="flex flex-wrap gap-1.5">
      {value.added.map((tag, index) => (
        <DiffChip key={`added-${index}`} label={tag} operationLabel={diffOperationLabels.added} tone="added" />
      ))}
      {value.removed.map((tag, index) => (
        <DiffChip key={`removed-${index}`} label={tag} operationLabel={diffOperationLabels.removed} tone="removed" />
      ))}
    </div>
  );
}

/**
 * Dispatches to the renderer matching the entry's value kind. When both `previousValue` and
 * `newValue` are `null` (no typed value carried by this entry), or the kind is not one of the
 * known `HistoryValue` kinds, no value row is rendered — the entry still shows its label/actor/timestamp.
 */
function renderEntryValue(entry: HistoryFeedEntry, context: HistoryFeedRenderContext): React.ReactNode {
  const kind = entry.newValue?.kind ?? entry.previousValue?.kind;
  if (kind === undefined) return null;

  const renderer: ValueRenderer | undefined = VALUE_RENDERERS[kind];
  if (renderer === undefined) return null;

  return renderer(entry.previousValue, entry.newValue, context);
}

// ─── Row & skeleton ───────────────────────────────────────────────────────────

interface HistoryFeedRowProps {
  readonly context: HistoryFeedRenderContext;
  readonly entry: HistoryFeedEntry;
}

function HistoryFeedRow({ context, entry }: HistoryFeedRowProps): React.JSX.Element {
  const content = renderEntryValue(entry, context);

  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <CircleDot aria-hidden="true" className="h-3 w-3 shrink-0 text-success" />
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-sm text-foreground">
            <span>{entry.label}</span>
            <span aria-hidden="true">·</span>
            <span className="text-muted-foreground">{entry.actorLabel}</span>
          </div>
          <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
            {entry.timestamp}
          </span>
        </div>
      </div>
      {content !== null && <div className="pl-5">{content}</div>}
    </li>
  );
}

interface HistoryFeedSkeletonProps {
  readonly className?: string;
  readonly rowCount: number;
}

function HistoryFeedSkeleton({ className, rowCount }: HistoryFeedSkeletonProps): React.JSX.Element {
  return (
    <ol className={cn('space-y-2', className)}>
      {Array.from({ length: rowCount }, (_, index) => (
        <li className="flex flex-col gap-1.5" key={index}>
          <div className="flex items-center gap-2">
            <Skeleton className="h-3 w-3 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <Skeleton className="h-3 w-1/3" />
              <Skeleton className="h-3 w-16 shrink-0" />
            </div>
          </div>
          <Skeleton className="ml-5 h-4 w-2/3" />
        </li>
      ))}
    </ol>
  );
}
